import type { NuxtUiTheme } from '../types/nuxt-ui-theme';
import { progressChipLabelStyles, truncatedChipLabelStyles, progressChipControlStyles } from '../shared-styles';

const baseStyles = 'gap-[0.4rem]';
const optionButtonBaseStyles =
    'relative gap-[0.55rem] px-[1.2rem] py-[0.6rem] text-[0.8rem] font-bold rounded-[62.438rem] ' +
    'border-[0.063rem] border-(--glass-chip-border) bg-(--glass-chip-bg) ' +
    '[backdrop-filter:blur(0.875rem)_saturate(160%)] text-[rgba(71,85,105,0.82)] cursor-pointer text-left ' +
    'font-(family-name:--secondary-font) ' +
    'shadow-[0_0.125rem_0.5rem_rgba(100,116,139,0.12),inset_0_0.063rem_0_rgba(255,255,255,var(--glass-inset))] ' +
    'transition-[box-shadow,transform,color,border-color,background] duration-300 ease-[ease] ' +
    'hover:text-(--input-neumorphic-text) hover:border-(--glass-chip-border-hover) ' +
    'hover:bg-(--glass-chip-bg-hover) ' +
    'hover:shadow-[0_0.25rem_1rem_rgba(100,116,139,0.18),0_0_0.875rem_rgba(100,116,139,0.1),inset_0_0.063rem_0_rgba(255,255,255,var(--glass-inset))] ' +
    'hover:translate-y-[-0.063rem] active:translate-y-[0.063rem] active:scale-[0.97] ' +
    'active:shadow-[0_0.063rem_0.25rem_rgba(100,116,139,0.12),inset_0_0.063rem_0_rgba(255,255,255,var(--glass-inset))]';
const optionButtonLeadingIconStyles =
    'flex items-center shrink-0 w-4 h-4 text-(--text-medium) transition-colors duration-200';
const optionButtonLabelStyles = 'flex-1 min-w-0 leading-[1.35] text-(--text-medium)';
const optionButtonActiveStyles = {
    base:
        'bg-(image:--accent-gradient) text-(--pure-white) border-transparent shadow-(--accent-control-active-shadow) ' +
        'translate-y-[-0.063rem] hover:bg-(image:--accent-gradient) hover:-translate-y-0.5 ' +
        'hover:shadow-[0_0.375rem_1.375rem_var(--progress-glow),inset_0_0.063rem_0_rgba(255,255,255,var(--glass-inset))]',
    leadingIcon: 'text-(--pure-white)',
    label: 'text-(--pure-white) font-bold'
};
const neumorphicButtonRootStyles =
    'relative cursor-pointer rounded-[100em]! border-0 px-[1.4em] py-[0.6em] gap-[0.4em]!';
const neumorphicButtonTransitionStyles = 'transition-shadow duration-300 ease-[ease]! hover:not-disabled:shadow-none';
const neumorphicButtonSurfaceBaseStyles =
    'before:content-[""] before:absolute before:inset-0 before:z-0 before:rounded-[inherit]';
const neumorphicButtonSurfaceTransitionStyles =
    'before:transition-[box-shadow,clip-path,transform] before:duration-250 before:ease-[ease]';
const neumorphicButtonContentMotionStyles = '*:relative *:z-1 *:duration-250 *:ease-[ease] *:select-none';
const neumorphicButtonLabelStyles =
    '**:data-[slot=label]:font-(family-name:--primary-font) **:data-[slot=label]:font-semibold ' +
    '**:data-[slot=label]:tracking-[-0.01em] **:data-[slot=label]:whitespace-nowrap ' +
    '**:data-[slot=label]:text-[rgba(0,0,0,0)]';
const neumorphicButtonDefaultSlotStyles =
    '[&>span:not([data-slot])]:font-(family-name:--primary-font) [&>span:not([data-slot])]:font-semibold ' +
    '[&>span:not([data-slot])]:tracking-[-0.01em] [&>span:not([data-slot])]:whitespace-nowrap ' +
    '[&>span:not([data-slot])]:text-[rgba(0,0,0,0)]';
const neumorphicButtonDarkIconStyles =
    '**:data-[slot=leadingIcon]:shrink-0 **:data-[slot=leadingIcon]:text-(--shadow-light) ' +
    '**:data-[slot=leadingIcon]:drop-shadow-(--control-icon-shadow) **:data-[slot=trailingIcon]:shrink-0 ' +
    '**:data-[slot=trailingIcon]:text-(--shadow-light) ' +
    '**:data-[slot=trailingIcon]:drop-shadow-(--control-icon-shadow)';
const neumorphicButtonDisabledStyles = 'disabled:opacity-50 disabled:grayscale-[0.3] disabled:transform-none';

export default {
    variants: {
        variant: {
            link: 'cursor-pointer',
            action:
                'relative flex size-8 cursor-pointer justify-center rounded-full border-[0.063rem] ' +
                'border-(--input-neumorphic-border) bg-(image:--input-neumorphic-surface) p-0 ' +
                'shadow-(--input-neumorphic-icon-well-shadow) outline-none ' +
                'transition-[filter,transform,background,border-color,color,box-shadow] duration-300 ' +
                'ease-[cubic-bezier(0.25,0.8,0.25,1)] ' +
                'will-change-[filter,transform,background,border-color,color,box-shadow] hover:not-disabled:z-2 ' +
                'hover:not-disabled:-translate-y-0.5 hover:not-disabled:scale-[1.06] ' +
                'hover:not-disabled:border-(--input-neumorphic-border-hover) ' +
                'hover:not-disabled:bg-(image:--input-neumorphic-surface-active) ' +
                'hover:not-disabled:shadow-(--input-neumorphic-icon-well-hover-shadow) focus-visible:outline-none ' +
                'focus-visible:shadow-(--input-neumorphic-focus-shadow) active:not-disabled:translate-y-[0.063rem] ' +
                'active:not-disabled:scale-[0.9] active:not-disabled:border-(--input-neumorphic-border-active) ' +
                'active:not-disabled:bg-(image:--input-neumorphic-icon-well-active-surface) ' +
                'active:not-disabled:shadow-(--input-neumorphic-icon-well-active-shadow) ' +
                'active:not-disabled:filter-none hover:active:not-disabled:translate-y-[0.063rem] ' +
                'hover:active:not-disabled:scale-[0.9] ' +
                'hover:active:not-disabled:border-(--input-neumorphic-border-active) ' +
                'hover:active:not-disabled:bg-(image:--input-neumorphic-icon-well-active-surface) ' +
                'hover:active:not-disabled:shadow-(--input-neumorphic-icon-well-active-shadow)',
            glow:
                'w-full bg-white text-black font-(family-name:--secondary-font) font-bold text-[0.875rem] uppercase ' +
                'p-5! rounded-[1rem] cursor-pointer shadow-(--button-glow-shadow) ' +
                '[transition:background-color_var(--transition-smooth),box-shadow_var(--transition-smooth),transform_var(--transition-smooth),letter-spacing_var(--transition-smooth)] ' +
                'will-change-[background-color,box-shadow,transform,letter-spacing] ' +
                'hover:not-disabled:tracking-[0.08em] hover:not-disabled:bg-white ' +
                'hover:not-disabled:shadow-(--button-glow-hover-shadow) hover:not-disabled:scale-[1.02] ' +
                'focus:outline-none focus:shadow-(--button-glow-focus-shadow) active:not-disabled:scale-[0.96] ' +
                'active:not-disabled:shadow-(--button-glow-active-shadow) disabled:opacity-90',
            'auth-footer':
                'cursor-pointer rounded-full border border-white/25 bg-white/5 px-5 py-2 font-semibold text-white ' +
                'hover:not-disabled:border-white/50 hover:not-disabled:bg-white/10 focus-visible:outline-2 ' +
                'focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-50',
            dark:
                neumorphicButtonRootStyles +
                ' bg-(--button-dark-bg) shadow-(--surface-dark-button-shadow) ' +
                neumorphicButtonTransitionStyles +
                ' ' +
                neumorphicButtonSurfaceBaseStyles +
                ' active:not-disabled:before:scale-[0.975] before:bg-(image:--button-dark-surface) ' +
                'before:[clip-path:inset(0_0_0_0_round_100em)] before:shadow-(--surface-dark-button-inset-shadow) ' +
                neumorphicButtonSurfaceTransitionStyles +
                ' active:not-disabled:before:shadow-(--surface-dark-button-inset-hover-shadow) ' +
                'active:not-disabled:before:[clip-path:inset(0.063rem_round_100em)] *:inline-flex *:items-center ' +
                '*:transition-transform ' +
                neumorphicButtonContentMotionStyles +
                ' active:not-disabled:*:scale-[0.955] ' +
                neumorphicButtonDarkIconStyles +
                ' ' +
                neumorphicButtonLabelStyles +
                ' **:data-[slot=label]:bg-(image:--button-dark-label-gradient) **:data-[slot=label]:bg-clip-text ' +
                neumorphicButtonDefaultSlotStyles +
                ' [&>span:not([data-slot])]:bg-(image:--button-dark-label-gradient) ' +
                '[&>span:not([data-slot])]:bg-clip-text ' +
                neumorphicButtonDisabledStyles +
                ' active:not-disabled:[&_.iconify_[data-slot=leadingIcon]]:scale-[0.955] ' +
                'active:not-disabled:[&_.iconify_[data-slot=leadingIcon]]:translate-x-0.5 ' +
                'active:not-disabled:[&_.iconify_[data-slot=trailingIcon]]:scale-[0.955] ' +
                'active:not-disabled:[&_.iconify_[data-slot=trailingIcon]]:-translate-x-0.5',
            danger:
                neumorphicButtonRootStyles +
                ' bg-[rgba(40,8,8,0.55)] ' +
                'shadow-[-0.125rem_-0.188rem_0.25rem_-0.063rem_rgba(120,15,15,0.18),0_0.063rem_0.063rem_0_rgba(120,15,15,0.18),0.063rem_0.125rem_0.188rem_0_rgba(120,15,15,0.06)] ' +
                neumorphicButtonTransitionStyles +
                ' ' +
                neumorphicButtonSurfaceBaseStyles +
                ' active:not-disabled:before:scale-[0.975] before:bg-(image:--button-danger-surface) ' +
                'before:[clip-path:inset(0_0_0_0_round_100em)] ' +
                'before:shadow-[inset_0_-0.125rem_0.25rem_0_rgba(80,5,5,0.12),inset_-0.125rem_-0.125rem_0.188rem_0_rgba(80,5,5,0.3),inset_0_0_0.125rem_0.25rem_rgba(255,255,255,0.06),inset_0.063rem_0.125rem_0.188rem_0_rgba(255,255,255,0.15),inset_0.125rem_0.125rem_0.188rem_rgba(255,255,255,0.06),inset_-0.125rem_-0.313rem_0.375rem_0.188rem_rgba(80,5,5,0.25)] ' +
                neumorphicButtonSurfaceTransitionStyles +
                ' active:not-disabled:before:[clip-path:inset(0.063rem_round_100em)] ' +
                'active:not-disabled:before:shadow-[inset_0.125rem_0.188rem_0.125rem_0_rgba(80,5,5,0.7),inset_-0.063rem_-0.125rem_0.188rem_0.063rem_rgba(80,5,5,0.5),inset_0.25rem_0.313rem_0.25rem_0_rgba(80,5,5,0.5),inset_0_0_0.125rem_0.5rem_rgba(255,255,255,0.04),inset_0.125rem_0.125rem_0.188rem_rgba(255,255,255,0.06),inset_-0.125rem_-0.188rem_0.313rem_0.188rem_rgba(80,5,5,0.25)] ' +
                '*:inline-flex *:items-center *:transition-transform ' +
                neumorphicButtonContentMotionStyles +
                ' active:not-disabled:*:scale-[0.955] ' +
                neumorphicButtonDarkIconStyles +
                ' ' +
                neumorphicButtonLabelStyles +
                ' **:data-[slot=label]:bg-(image:--button-danger-label-gradient) **:data-[slot=label]:bg-clip-text ' +
                neumorphicButtonDefaultSlotStyles +
                ' [&>span:not([data-slot])]:bg-(image:--button-danger-label-gradient) ' +
                '[&>span:not([data-slot])]:bg-clip-text ' +
                neumorphicButtonDisabledStyles +
                ' active:not-disabled:[&_.iconify_[data-slot=leadingIcon]]:scale-[0.955] ' +
                'active:not-disabled:[&_.iconify_[data-slot=leadingIcon]]:translate-x-0.5 ' +
                'active:not-disabled:[&_.iconify_[data-slot=trailingIcon]]:scale-[0.955] ' +
                'active:not-disabled:[&_.iconify_[data-slot=trailingIcon]]:-translate-x-0.5',
            light:
                neumorphicButtonRootStyles +
                ' bg-[rgba(90,97,112,0.55)] ' +
                'shadow-[-0.125rem_-0.188rem_0.25rem_-0.063rem_rgba(5,5,5,0.12),0_0.063rem_0.063rem_0_rgba(5,5,5,0.18),0.063rem_0.125rem_0.188rem_0_rgba(5,5,5,0.06)] ' +
                neumorphicButtonTransitionStyles +
                ' ' +
                neumorphicButtonSurfaceBaseStyles +
                ' before:bg-(image:--button-light-surface) ' +
                'before:shadow-[inset_0_-0.125rem_0.25rem_0_rgba(5,5,5,0.08),inset_-0.125rem_-0.125rem_0.188rem_0_rgba(5,5,5,0.15),inset_0_0_0.125rem_0.25rem_rgba(255,255,255,0.3),inset_0.063rem_0.125rem_0.188rem_0_rgba(255,255,255,1),inset_0.125rem_0.125rem_0.188rem_rgba(255,255,255,0.3),inset_-0.125rem_-0.313rem_0.375rem_0.188rem_rgba(5,5,5,0.15)] ' +
                neumorphicButtonSurfaceTransitionStyles +
                ' active:not-disabled:before:shadow-[inset_0.25rem_0.25rem_0.625rem_rgba(15,23,42,0.2),inset_-0.25rem_-0.25rem_0.625rem_rgba(255,255,255,0.7)] ' +
                '*:inline-flex! *:items-center *:transition-transform! ' +
                neumorphicButtonContentMotionStyles +
                ' active:not-disabled:*:scale-[0.975] active:not-disabled:[&_.iconify]:scale-[0.955] ' +
                'active:not-disabled:[&_.iconify]:translate-x-0.5 **:data-[slot=leadingIcon]:shrink-0 ' +
                '**:data-[slot=leadingIcon]:text-(--text-button-light) ' +
                '**:data-[slot=leadingIcon]:drop-shadow-(--control-icon-shadow) **:data-[slot=trailingIcon]:shrink-0 ' +
                '**:data-[slot=trailingIcon]:text-(--text-button-light) ' +
                '**:data-[slot=trailingIcon]:drop-shadow-(--control-icon-shadow) ' +
                neumorphicButtonLabelStyles +
                ' **:data-[slot=label]:bg-(image:--button-light-label-gradient) **:data-[slot=label]:bg-clip-text ' +
                neumorphicButtonDefaultSlotStyles +
                ' [&>span:not([data-slot])]:bg-(image:--button-light-label-gradient) ' +
                '[&>span:not([data-slot])]:bg-clip-text ' +
                neumorphicButtonDisabledStyles,
            capsule:
                'gap-[0.3rem] px-[0.7rem] py-[0.3rem] rounded-[6.188rem] border-0 bg-(image:--accent-gradient) ' +
                'text-(--pure-white) font-(family-name:--secondary-font) text-[0.688rem] font-bold tracking-[0.02em] ' +
                'leading-[110%] whitespace-nowrap cursor-pointer shadow-(--progress-glow-shadow-sm) ' +
                'transition-[transform,box-shadow,opacity] duration-200 ease-in-out ' +
                'will-change-[transform,box-shadow] ' +
                'hover:not-disabled:shadow-[0_0.25rem_0.75rem_var(--progress-glow),0_0_0_0.125rem_rgba(99,102,241,0.15)] ' +
                'active:not-disabled:scale-[0.94] active:not-disabled:shadow-(--progress-glow-shadow-2xs) ' +
                'disabled:opacity-45',
            stealth:
                'flex justify-center w-[1.2rem] h-[1.2rem] shrink-0 rounded-full border-0 p-0 cursor-pointer ' +
                'bg-(--slate-alpha-10) text-(--text-muted) transition-[background] duration-150 ease-in ' +
                'hover:bg-(--slate-alpha-20)',
            'admin-action':
                'size-8! flex cursor-pointer justify-center rounded-xl border border-transparent p-0! text-slate-400 ' +
                'disabled:opacity-50',
            'icon-action':
                'size-8! p-0! flex justify-center shrink-0 grow-0 rounded-full cursor-pointer border ' +
                'border-transparent text-slate-400 disabled:opacity-50 **:data-[slot=leadingIcon]:shrink-0! ' +
                '**:data-[slot=leadingIcon]:text-current',
            'icon-control':
                'flex shrink-0 cursor-pointer justify-center rounded-xl border border-transparent bg-transparent ' +
                'p-0! text-gray-500 shadow-none outline-none transition-[background-color,box-shadow] duration-300 ' +
                'ease-[cubic-bezier(0.25,0.8,0.25,1)] will-change-[background-color,box-shadow] ' +
                'hover:bg-(--glass-surface-bg-62) hover:shadow-(--input-neumorphic-icon-well-hover-shadow) ' +
                'focus-visible:shadow-(--input-neumorphic-focus-shadow) active:bg-(--glass-surface-bg-78) ' +
                'active:shadow-(--input-neumorphic-icon-well-active-shadow) hover:active:bg-(--glass-surface-bg-78) ' +
                'hover:active:shadow-(--input-neumorphic-icon-well-active-shadow)',
            'chat-send':
                'flex h-full w-16 shrink-0 justify-center rounded-none! border-0 bg-blue-500 text-white p-0 ' +
                'shadow-[inset_0_1px_0_rgba(255,255,255,0.15),inset_0_-1px_0_rgba(0,0,0,0.15),0_4px_12px_rgba(59,130,246,0.25)] ' +
                'transition-[background-color,transform,box-shadow] duration-200 ease-in-out will-change-transform ' +
                'hover:not-disabled:bg-blue-400 ' +
                'hover:not-disabled:shadow-[inset_0_1px_0_rgba(255,255,255,0.2),inset_0_-1px_0_rgba(0,0,0,0.15),0_6px_18px_rgba(96,165,250,0.35)] ' +
                'active:not-disabled:scale-[0.96] ' +
                'active:not-disabled:shadow-[inset_0_2px_4px_rgba(0,0,0,0.25),0_2px_8px_rgba(59,130,246,0.2)] ' +
                'disabled:bg-slate-700 disabled:text-slate-400 disabled:shadow-none disabled:opacity-100 ' +
                '*:transition-transform *:duration-200 *:ease-in-out hover:not-disabled:*:scale-[0.96] ' +
                'active:not-disabled:*:scale-[0.9] **:data-[slot=leadingIcon]:size-6',
            'chat-send-light':
                'flex h-full w-16 shrink-0 justify-center rounded-none! border-0 bg-white text-slate-700 p-0 ' +
                'shadow-[inset_0_1px_0_rgba(255,255,255,0.85),inset_0_-1px_0_rgba(15,23,42,0.08),0_4px_12px_rgba(15,23,42,0.12)] ' +
                'transition-[background-color,transform,box-shadow] duration-200 ease-in-out will-change-transform ' +
                'hover:not-disabled:bg-slate-100 ' +
                'hover:not-disabled:shadow-[inset_0_1px_0_rgba(255,255,255,0.95),inset_0_-1px_0_rgba(15,23,42,0.1),0_6px_18px_rgba(15,23,42,0.16)] ' +
                'active:not-disabled:scale-[0.96] ' +
                'active:not-disabled:shadow-[inset_0_2px_4px_rgba(15,23,42,0.16),0_2px_8px_rgba(15,23,42,0.1)] ' +
                'disabled:bg-slate-100 disabled:text-slate-400 disabled:shadow-none disabled:opacity-100 ' +
                '*:transition-transform *:duration-200 *:ease-in-out hover:not-disabled:*:scale-[0.96] ' +
                'active:not-disabled:*:scale-[0.9] **:data-[slot=leadingIcon]:size-6',
            option: {
                base: optionButtonBaseStyles,
                leadingIcon: optionButtonLeadingIconStyles,
                label: optionButtonLabelStyles
            },
            'preset-package': {
                base:
                    optionButtonBaseStyles +
                    ' justify-center shadow-(--surface-card-shadow)! ' +
                    'hover:shadow-[0.5rem_0.5rem_1.125rem_rgba(163,177,198,0.42),-0.5rem_-0.5rem_1.125rem_rgba(255,255,255,0.96),inset_0_0.063rem_0_rgba(255,255,255,0.75)]! ' +
                    'active:translate-y-[0.063rem] active:scale-[0.99]! ' +
                    'active:shadow-(--preset-package-button-active-shadow)! ' +
                    'hover:active:shadow-(--preset-package-button-active-shadow)!',
                leadingIcon: optionButtonLeadingIconStyles,
                label: optionButtonLabelStyles
            },
            'substep-chip': {
                base:
                    baseStyles +
                    ' px-[0.8rem] py-[0.38rem] rounded-[62.438rem] bg-(--glass-surface-bg-45) ' +
                    '[backdrop-filter:blur(0.875rem)_saturate(160%)] border-[0.094rem] border-[rgba(71,85,105,0.13)] ' +
                    'shadow-[0_0.125rem_0.375rem_rgba(100,116,139,0.07),inset_0_0.063rem_0_rgba(255,255,255,var(--glass-inset))] ' +
                    'cursor-pointer text-[0.72rem] font-bold text-(--text-muted) transition-all duration-[0.22s] ' +
                    'ease-in-out not-disabled:hover:opacity-90 ' +
                    'not-disabled:hover:shadow-[0_0.25rem_0.75rem_rgba(100,116,139,0.12),inset_0_0.063rem_0_rgba(255,255,255,var(--glass-inset))] ' +
                    'disabled:transform-none!',
                label: truncatedChipLabelStyles
            },
            chip: {
                base:
                    'relative ' +
                    baseStyles +
                    ' ' +
                    progressChipControlStyles +
                    ' opacity-[0.52] shrink-0 transition-all duration-250 ease-in-out ' +
                    'not-disabled:hover:bg-(--glass-surface-bg-50) not-disabled:hover:opacity-[0.85]',
                label: progressChipLabelStyles
            }
        }
    },
    compoundVariants: [
        {
            variant: 'option',
            active: true,
            class: optionButtonActiveStyles
        },
        {
            variant: 'preset-package',
            active: true,
            class: optionButtonActiveStyles
        },
        {
            variant: 'preset-package',
            active: true,
            class: {
                base:
                    'shadow-(--surface-dark-button-shadow)! hover:shadow-(--surface-dark-button-hover-shadow)! ' +
                    'active:translate-y-[0.063rem]! active:scale-[0.99]! ' +
                    'active:shadow-(--preset-package-button-selected-active-shadow)! ' +
                    'hover:active:translate-y-[0.063rem]! ' +
                    'hover:active:shadow-(--preset-package-button-selected-active-shadow)!'
            }
        }
    ]
} satisfies NuxtUiTheme['button'];
